import {
    ChangeDetectorRef,
    Directive,
    Input,
    OnDestroy,
    OnInit,
    inject
} from "@angular/core";
import { Anskaffningsformer, VirkesorderStatus } from "~app/common/models/constants";
import { IBridgeEvent } from "~app/common/services/event-bridge.service";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IArbetsorderDetail } from "~app/features/planera/model/IArbetsorderDetail";
import { IAtgardWithUtbyte } from "~app/features/planera/model/IAtgard";
import { environment } from "~environments/environment";
import { AtgardAddableBehaviour } from "../behaviours/AtgardAddableBehaviour";
import { MapEvents } from "../events/map-events";
import { ArbetsorderService, Produktionsstatusar } from "../services/arbetsorder.service";
import { AtgardPlaneringsstatusService } from "../services/atgard-planeringsstatus.service";
import { AtgardService } from "../services/atgard.service";
import { PlaneraKartaEventService } from "./planera-karta-event.service";

@Directive()
export class PlaneraAtgardBaseComponent implements OnInit, OnDestroy  {
    arbetsorder: IArbetsorderDetail;
    atgard: IAtgardWithUtbyte;
    atgardAddable: AtgardAddableBehaviour;
    isAtgardAddable: boolean;
    selectedArbetsorder: IArbetsorderDetail;

    protected readonly arbetsorderService = inject(ArbetsorderService);
    protected readonly atgardPlaneringsstatusService = inject(AtgardPlaneringsstatusService);
    protected readonly atgardService = inject(AtgardService);
    protected readonly changeDetectorRef = inject(ChangeDetectorRef);
    protected readonly planeraKartaEventService = inject(PlaneraKartaEventService);

    private readonly planeraKartaEvents: IBridgeEvent[] = [];

    @Input()
    set data(value: any) {
        this.arbetsorder = value.arbetsorder;
        this.selectedArbetsorder = value.selectedArbetsorder;
        if (value.atgard.namn === undefined) {
            this.atgardService.getAtgard(value.atgard.guid).subscribe(r => {
                this.atgard = r;
                this.isAtgardAddableCheck();
                this.changeDetectorRef.detectChanges();
            });
        } else {
            this.atgard = value.atgard;
            this.isAtgardAddableCheck();
        }
    }

    addAtgardToArbetsorder() {
        this.planeraKartaEventService.emit(MapEvents.addAtgardToArbetsorder, { atgard: this.atgard,
            arbetsorder: this.selectedArbetsorder });
    }

    canAddAtgardToArbetsorder(): boolean {
        if (this.arbetsorder.internProduktionsstatus > Produktionsstatusar.produktionEjPaborjad ||
            this.selectedArbetsorder?.internProduktionsstatus > Produktionsstatusar.produktionEjPaborjad) {
            return false;
        }

        if (this.isActiveVo() && this.arbetsorder.atgarder.length === 1) {
            return false;
        }

        return this.atgardAddable ? this.atgardAddable.isAtgardAddable() : false;
    }

    existInNavetApp() {
        const isGrotskotning = this.arbetsorder && this.arbetsorder.isGrotskotning;
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (this.arbetsorder.anskaffningsform === Anskaffningsformer.Egenskog
            && !isGrotskotning) {
            return true;
        }

        return false;
    }

    isAtgardAddableCheck() {
        this.atgardAddable = new AtgardAddableBehaviour(this.atgard, this.selectedArbetsorder, this.atgardPlaneringsstatusService);
        this.isAtgardAddable = this.canAddAtgardToArbetsorder();
    }

    navetAppLink = (): string => `${environment.navetAppUrl}atgard/${this.atgard.beteckning}`;

    ngOnDestroy(): void {
        this.planeraKartaEventService.unsubscribeMany(this.planeraKartaEvents);
        this.planeraKartaEvents.splice(0);
    }

    ngOnInit(): void {
        this.planeraKartaEvents.push(this.planeraKartaEventService.subscribe(MapEvents.arbetsorderUpdated, (ao) => {
            this.selectedArbetsorder = ao as IArbetsorderDetail;
            this.isAtgardAddableCheck();
        }));
    }

    private isActiveVo() {
        if (!ObjectUtils.hasValue(this.arbetsorder.virkesorderStatus)) {
            return false;
        }

        return this.arbetsorder.virkesorderStatus.some(x => x.status === VirkesorderStatus.Aktiv || VirkesorderStatus.Makulerad);
    }
}
